import { ApiRequest } from '../baseService'

const BaseRoute = 'app/system-config'

function GetSystemConfig(tenantId?: string) {
  return ApiRequest.get(
    `${BaseRoute}${tenantId ? '?tenantId=' + tenantId : ''}`
  )
}

function UpdateSystemConfig(payload, tenantId?) {
  return ApiRequest.put(
    `${BaseRoute}${tenantId ? '?tenantId=' + tenantId : ''}`,
    payload
  )
}

function GetSetting(key = '') {
  return ApiRequest.get(`${BaseRoute}/getTenantSetting?key=${key}`)
}

// Staff IP allowlist (host management, per tenant)
function GetStaffIpAllowlist(tenantId: string) {
  return ApiRequest.get(`${BaseRoute}/staff-ip-allowlist?tenantId=${tenantId}`)
}

function UpdateStaffIpAllowlist(tenantId: string, payload) {
  return ApiRequest.put(
    `${BaseRoute}/staff-ip-allowlist?tenantId=${tenantId}`,
    payload
  )
}

// Access Restriction (host management, per tenant) — per-role rules (IP / range / country)
function GetAccessRestriction(tenantId: string) {
  return ApiRequest.get(`${BaseRoute}/access-restriction?tenantId=${tenantId}`)
}

function UpdateAccessRestriction(tenantId: string, payload) {
  return ApiRequest.put(
    `${BaseRoute}/access-restriction?tenantId=${tenantId}`,
    payload
  )
}

function GetAccessRestrictionCountries() {
  return ApiRequest.get(`${BaseRoute}/access-restriction/countries`)
}

// Health check + current caller IP/country (to verify GeoIP setup and self-check)
function GetAccessRestrictionDiagnostics() {
  return ApiRequest.get(`${BaseRoute}/access-restriction/diagnostics`)
}

// Test the tenant's saved Microsoft Entra ID credentials by hitting the Entra
// token endpoint with the client_credentials grant. tenantId is optional —
// host admins can pass an explicit tenantId; tenant admins are scoped to their
// own tenant by the backend.
function TestEntraIdConnection(tenantId?: string) {
  return ApiRequest.post(
    `${BaseRoute}/test-entra-id-connection${
      tenantId ? `?tenantId=${tenantId}` : ''
    }`,
    {}
  )
}

// Microsoft Purview label handling (host management, per tenant).
// The dedicated endpoints exist because the domain list embeds a client secret per domain:
// GET returns every secret masked as '***', and sending '***' back on PUT keeps the stored value.
function GetPurviewProtection(tenantId: string) {
  return ApiRequest.get(`${BaseRoute}/purview-protection?tenantId=${tenantId}`)
}

function UpdatePurviewProtection(tenantId: string, payload) {
  return ApiRequest.put(
    `${BaseRoute}/purview-protection?tenantId=${tenantId}`,
    payload
  )
}

// Verifies one domain against Microsoft (token + label policy) without needing a test upload.
function TestPurviewConnection(tenantId: string, payload) {
  return ApiRequest.post(
    `${BaseRoute}/purview-protection/test-connection?tenantId=${tenantId}`,
    payload
  )
}

// Pushes a throwaway file through the real decryption pipeline and reports what happened.
// The server stores nothing — no blob, no attachment, no storage quota impact.
function TestPurviewDecrypt(tenantId: string, formData: FormData) {
  return ApiRequest.post(
    `${BaseRoute}/purview-protection/test-decrypt?tenantId=${tenantId}`,
    formData
  )
}

// Same diagnostic, but returns the decrypted file itself so the administrator can open it and
// confirm the content survived. The server re-runs the decryption and stores nothing, so the file
// has to be posted again rather than referenced by an id.
function TestPurviewDecryptDownload(tenantId: string, formData: FormData) {
  return ApiRequest.post(
    `${BaseRoute}/purview-protection/test-decrypt/download?tenantId=${tenantId}`,
    formData,
    { responseType: 'blob' }
  )
}

function GetSystemConfigForUser(key?: string) {
  return ApiRequest.get(`${BaseRoute}/user?${key ? `&key=${key}` : ''}`)
}

function UpdateSystemConfigUser(payload) {
  return ApiRequest.put(`${BaseRoute}/user`, payload)
}

function UpdateUserTableSetting(payload) {
  return ApiRequest.put(`${BaseRoute}/updateUserTableSetting`, payload)
}

function GetUserTableSettings() {
  return ApiRequest.get(`${BaseRoute}/userTableSettings`)
}

function RemoveConfigForCurrentUser(key: string) {
  return ApiRequest.delete(`${BaseRoute}/removeCurrentUserSetting?key=${key}`)
}

// Verified SendGrid senders for the "Sender email" list of the Top Provider
// Availability Email settings. Deliberately NOT the marketing-campaign
// endpoint: that one sits behind the MarketingCampaign feature and answered
// 403 for every tenant without it, even though this setting is unrelated.
function GetSenders() {
  return ApiRequest.get(`${BaseRoute}/senders`)
}

const API = {
  GetSystemConfig,
  UpdateSystemConfig,
  GetSetting,
  GetStaffIpAllowlist,
  UpdateStaffIpAllowlist,
  GetAccessRestriction,
  UpdateAccessRestriction,
  GetAccessRestrictionCountries,
  GetAccessRestrictionDiagnostics,
  TestEntraIdConnection,
  GetPurviewProtection,
  UpdatePurviewProtection,
  TestPurviewConnection,
  TestPurviewDecrypt,
  TestPurviewDecryptDownload,
  GetSystemConfigForUser,
  UpdateSystemConfigUser,
  UpdateUserTableSetting,
  GetUserTableSettings,
  RemoveConfigForCurrentUser,
  GetSenders,
}

export default API
